import { Phone, Mail, MapPin, Heart } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { NAV_LINKS, SERVICES, CONTACT_INFO } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

export const Footer = () => (
  <footer data-testid="footer" className="bg-navy-deep text-cream/80">
    <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Wordmark dark testId="footer-wordmark" />
          <p className="mt-5 max-w-xs font-serif text-base italic leading-relaxed text-cream/70">
            Bringing comfort, connection and companionship to every moment.
          </p>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-cream/55">
            Dignified non-medical companionship for older adults across the United Kingdom.
          </p>
        </div>

        <nav className="lg:col-span-2" aria-label="Footer navigation">
          <h3 className="font-mono text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-gold">
            Explore
          </h3>
          <ul className="mt-5 space-y-3">
            {NAV_LINKS.map((link) => (
              <li key={link.testId}>
                <a
                  href={link.target}
                  onClick={(e) => scrollToSection(e, link.target)}
                  data-testid={`footer-${link.testId}`}
                  className="text-sm text-cream/70 transition-colors hover:text-gold"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="lg:col-span-3">
          <h3 className="font-mono text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-gold">
            Companionship
          </h3>
          <ul className="mt-5 space-y-3">
            {SERVICES.map((s) => (
              <li key={s.id}>
                <a
                  href="#services"
                  onClick={(e) => scrollToSection(e, "#services")}
                  data-testid={`footer-service-${s.id}`}
                  className="text-sm text-cream/70 transition-colors hover:text-gold"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h3 className="font-mono text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-gold">
            Contact
          </h3>
          <ul className="mt-5 space-y-4 text-sm">
            <li data-testid="footer-phone" className="flex items-center gap-3">
              <Phone className="h-4 w-4 shrink-0 text-mauve" aria-hidden="true" />
              {CONTACT_INFO.phone} ({CONTACT_INFO.hours})
            </li>
            <li data-testid="footer-email" className="flex items-center gap-3">
              <Mail className="h-4 w-4 shrink-0 text-mauve" aria-hidden="true" />
              {CONTACT_INFO.email}
            </li>
            <li data-testid="footer-address" className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-mauve" aria-hidden="true" />
              <span>
                {CONTACT_INFO.address.map((line) => (
                  <span key={line} className="block">{line}</span>
                ))}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-14 rounded-2xl border border-cream/10 bg-cream/[0.04] p-6">
        <p className="text-xs leading-relaxed text-cream/55" data-testid="footer-disclaimer">
          Adeya Companions provides strictly non-medical companionship, social engagement,
          and lifestyle support. We do not provide clinical nursing, personal care, or
          regulated healthcare tasks.
        </p>
      </div>

      <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-cream/10 pt-8 sm:flex-row">
        <p className="text-xs text-cream/50">
          © 2026 Adeya Companions Ltd. All rights reserved.
        </p>
        <p className="flex items-center gap-1.5 text-xs text-cream/50">
          Made with <Heart className="h-3 w-3 fill-mauve text-mauve" aria-hidden="true" /> across the UK
        </p>
      </div>
    </div>
  </footer>
);
